/**
 * StandardFormModal - Default Form Modal Preset
 *
 * Standard configuration for most CRUD forms:
 * - Extra-large size (xl)
 * - Character counter enabled
 * - Dirty check enabled
 * - Keyboard shortcuts enabled
 * - Auto-focus first field
 * - Submit overlay
 *
 * Ideal for:
 * - CRUD operations (Customer, Order, Shipment, etc.)
 * - Standard data entry forms
 * - Most common use cases (80% of forms)
 *
 * @module standard-form-modal
 */

'use client';

import * as React from 'react';

import { EnhancedFormModal, type EnhancedFormModalProps } from '../enhanced-form-modal';

/**
 * StandardFormModal props (extends core but with preset defaults)
 */
export type StandardFormModalProps = Omit<
  EnhancedFormModalProps,
  'showCharacterCount' | 'enableKeyboardShortcuts' | 'autoFocusFirstField'
>;

/**
 * StandardFormModal - Standard form modal preset
 *
 * Default configuration for most CRUD forms with commonly used features enabled.
 * This is the recommended preset for 80% of use cases.
 *
 * @example
 * ```tsx
 * <StandardFormModal
 *   isOpen={isOpen}
 *   onClose={onClose}
 *   title="Add Customer"
 *   subtitle="Create a new customer record"
 *   enableDirtyCheck
 *   isDirty={isDirty}
 * >
 *   <CustomerForm />
 * </StandardFormModal>
 * ```
 *
 * @example With submit handling
 * ```tsx
 * <StandardFormModal
 *   isOpen={isOpen}
 *   onClose={onClose}
 *   title="Edit Order"
 *   isSubmitting={isSubmitting}
 *   enableDirtyCheck
 *   isDirty={isDirty}
 *   onCtrlEnter={handleSubmit}
 * >
 *   <OrderForm />
 * </StandardFormModal>
 * ```
 */
export function StandardFormModal(props: StandardFormModalProps) {
  return (
    <EnhancedFormModal
      {...props}
      // Preset configuration
      size={props.size ?? { base: 'full', md: 'xl' }}
      showCharacterCount={true}
      enableKeyboardShortcuts={true}
      autoFocusFirstField={true}
      // Accidental click-outside loses in-progress form data — industry
      // standard dialogs (Google, Stripe, Figma) require explicit Cancel/X
      // to dismiss. Caller can opt out with `preventClose={false}` for the
      // rare read-only / pickers where click-outside IS expected.
      preventClose={props.preventClose ?? true}
    />
  );
}

StandardFormModal.displayName = 'StandardFormModal';
